import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import {
  RecipeV2Detail,
  Allergen,
  DietaryAttribute,
  DietaryAttributeStatus,
  DietaryCategory,
  AllergenSource,
} from '../types';
import { UserContext } from '../../../components/shared/useUser';
import { isDemoModeEnabled } from '../../../utils/featureAccess';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import DietaryAttributeChips from './DietaryAttributeChips';

interface AllergensTabProps {
  recipe: RecipeV2Detail;
}

/** Categories shown on this tab - the two categories that trace back through components,
 * kept separate from the plain dietary attributes (see DietaryTab, which covers the other
 * four categories so the two tabs don't duplicate the same data). */
const ALLERGEN_TAB_CATEGORIES = [DietaryCategory.Allergen, DietaryCategory.Intolerance];

const statusColor: Record<DietaryAttributeStatus, 'error' | 'warning' | 'success' | 'default'> = {
  [DietaryAttributeStatus.Contains]: 'error',
  [DietaryAttributeStatus.NotSuitable]: 'error',
  [DietaryAttributeStatus.MayContain]: 'warning',
  [DietaryAttributeStatus.FreeFrom]: 'success',
  [DietaryAttributeStatus.Suitable]: 'success',
  [DietaryAttributeStatus.Certified]: 'success',
};

const lowerFirst = (value: string) => value.charAt(0).toLowerCase() + value.slice(1);

const AllergensTab = ({ recipe }: AllergensTabProps) => {
  const { t } = useTranslation('recipeV2');
  const { hasPermissionTo } = useContext(UserContext);
  const api = useRecipeV2Api();
  const canOverride = hasPermissionTo(['RecipeV2AllergenOverride']);
  const canApproveCompliance = hasPermissionTo(['ComplianceAllergenApprove']);
  const [complianceApproved, setComplianceApproved] = useState(false);

  const [allAttributes, setAllAttributes] = useState<DietaryAttribute[]>(recipe.dietaryAttributes ?? []);
  const [masterAllergens, setMasterAllergens] = useState<Allergen[]>([]);
  const attributes = allAttributes.filter((a) => ALLERGEN_TAB_CATEGORIES.includes(a.category));

  useEffect(() => {
    api.getDietaryAttributes(recipe.id).then(setAllAttributes).catch(() => {});
    api.getAllergens().then(setMasterAllergens).catch(() => {});
  }, [recipe.id, api]);

  // Override dialog
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<DietaryAttribute | null>(null);
  const [overrideStatus, setOverrideStatus] = useState<DietaryAttributeStatus>(DietaryAttributeStatus.FreeFrom);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideSaving, setOverrideSaving] = useState(false);

  // Add allergen/intolerance dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addCategory, setAddCategory] = useState<DietaryCategory>(DietaryCategory.Allergen);
  const [addAllergenId, setAddAllergenId] = useState<number | ''>('');
  const [addName, setAddName] = useState('');
  const [addStatus, setAddStatus] = useState<DietaryAttributeStatus>(DietaryAttributeStatus.Contains);

  const availableMasterAllergens = masterAllergens.filter(
    (a) => !attributes.some((existing) => existing.category === DietaryCategory.Allergen && existing.code === a.code),
  );

  const handleOpenOverride = (attr: DietaryAttribute) => {
    setSelectedAttribute(attr);
    setOverrideStatus(attr.status === DietaryAttributeStatus.Contains ? DietaryAttributeStatus.FreeFrom : DietaryAttributeStatus.Contains);
    setOverrideReason('');
    setOverrideDialogOpen(true);
  };

  const handleApplyOverride = async () => {
    if (!selectedAttribute) return;
    setOverrideSaving(true);
    try {
      await api.overrideDietaryAttribute(recipe.id, {
        attributeId: selectedAttribute.id,
        status: overrideStatus,
        reason: overrideReason,
      });
      setAllAttributes((prev) =>
        prev.map((a) => (a.id === selectedAttribute.id ? { ...a, status: overrideStatus } : a)),
      );
      setOverrideDialogOpen(false);
    } catch { /* noop */ }
    setOverrideSaving(false);
  };

  const handleOpenAdd = () => {
    setAddCategory(DietaryCategory.Allergen);
    setAddAllergenId('');
    setAddName('');
    setAddStatus(DietaryAttributeStatus.Contains);
    setAddDialogOpen(true);
  };

  const handleConfirmAdd = () => {
    const isAllergen = addCategory === DietaryCategory.Allergen;
    const master = isAllergen ? masterAllergens.find((a) => a.id === addAllergenId) : undefined;
    const name = isAllergen ? master?.name : addName.trim();
    const code = isAllergen ? master?.code : addName.trim().toUpperCase().replace(/\s+/g, '_');
    if (!name || !code) return;

    // lazy: client-side only, no backend persistence yet (matches overrideDietaryAttribute).
    setAllAttributes((prev) => [
      ...prev,
      {
        id: -Date.now(),
        code,
        name,
        category: addCategory,
        status: addStatus,
        source: AllergenSource.Manual,
      },
    ]);
    setAddDialogOpen(false);
  };

  if (attributes.length === 0) {
    return (
      <Stack spacing={3}>
        <Paper variant="outlined"
          sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('allergens.noAllergens')}</Typography>
        </Paper>
        {canOverride && (
          <Stack direction="row"
            justifyContent="flex-end">
            <Button startIcon={<Add />}
              variant="secondary"
              onClick={handleOpenAdd}>
              {t('allergens.add', 'Add allergen/intolerance')}
            </Button>
          </Stack>
        )}
        <Dialog open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          maxWidth="sm"
          fullWidth>
          <DialogTitle>{t('allergens.addTitle', 'Add allergen or intolerance')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2}
              sx={{ mt: 1 }}>
              <Select
                value={addCategory}
                onChange={(e) => setAddCategory(e.target.value as DietaryCategory)}
                size="small"
                fullWidth
              >
                <MenuItem value={DietaryCategory.Allergen}>{t('dietary.categories.allergen', 'Allergen')}</MenuItem>
                <MenuItem value={DietaryCategory.Intolerance}>{t('dietary.categories.intolerance', 'Intolerance')}</MenuItem>
              </Select>
              {addCategory === DietaryCategory.Allergen ? (
                <Select
                  value={addAllergenId}
                  onChange={(e) => setAddAllergenId(e.target.value as number)}
                  size="small"
                  displayEmpty
                  fullWidth
                >
                  <MenuItem value=""
                    disabled>{t('allergens.selectAllergen', 'Select an allergen')}</MenuItem>
                  {availableMasterAllergens.map((a) => (
                    <MenuItem key={a.id}
                      value={a.id}>{a.name}</MenuItem>
                  ))}
                </Select>
              ) : (
                <TextField
                  label={t('allergens.intoleranceName', 'Intolerance name')}
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  fullWidth
                />
              )}
              <Select
                value={addStatus}
                onChange={(e) => setAddStatus(e.target.value as DietaryAttributeStatus)}
                size="small"
                fullWidth
              >
                <MenuItem value={DietaryAttributeStatus.Contains}>{t('dietary.contains')}</MenuItem>
                <MenuItem value={DietaryAttributeStatus.MayContain}>{t('dietary.mayContain')}</MenuItem>
                <MenuItem value={DietaryAttributeStatus.FreeFrom}>{t('dietary.freeFrom')}</MenuItem>
              </Select>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)}
              variant="secondary">
              {t('allergens.overrideCancel')}
            </Button>
            <Button
              onClick={handleConfirmAdd}
              variant="primary"
              disabled={addCategory === DietaryCategory.Allergen ? addAllergenId === '' : addName.trim() === ''}
            >
              {t('allergens.addConfirm', 'Add')}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Compliance approval (real permission-gated) */}
      <Paper variant="outlined"
        sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Stack direction="row"
          spacing={2}
          alignItems="center">
          <Typography variant="textSM"
            fontWeight={600}>{t('allergens.complianceApproval', 'Compliance approval')}</Typography>
          <Chip
            label={complianceApproved ? t('allergens.approved', 'Approved') : t('allergens.pendingApproval', 'Pending approval')}
            color={complianceApproved ? 'success' : 'warning'}
            size="small"
          />
        </Stack>
        {!complianceApproved && canApproveCompliance && (
          <Button variant="primary"
            size="md"
            onClick={() => setComplianceApproved(true)}>
            {t('allergens.approveCompliance', 'Approve allergens & certs (Compliance)')}
          </Button>
        )}
        {!complianceApproved && !canApproveCompliance && isDemoModeEnabled() && (
          <Typography variant="textXS"
            color="text.secondary">
            {t('allergens.requiresComplianceRole', 'Requires the Compliance demo role to approve.')}
          </Typography>
        )}
      </Paper>

      {/* Summary chips - reuses the same tags as the dietary attribute "allergen profile" */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>{t('allergens.title')}</Typography>
        <DietaryAttributeChips
          attributes={attributes}
          categories={ALLERGEN_TAB_CATEGORIES}
          showName
          groupByCategory
          size="medium"
        />
      </Paper>

      {/* Traceability table */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center">
          <Typography variant="textXL"
            gutterBottom>{t('allergens.traceability')}</Typography>
          {canOverride && (
            <Button startIcon={<Add />}
              variant="secondary"
              size="md"
              onClick={handleOpenAdd}>
              {t('allergens.add', 'Add allergen/intolerance')}
            </Button>
          )}
        </Stack>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('allergens.allergen')}</TableCell>
                <TableCell>{t('statusLabel')}</TableCell>
                <TableCell>{t('source')}</TableCell>
                <TableCell>{t('allergens.introducedBy')}</TableCell>
                <TableCell>{t('allergens.path')}</TableCell>
                {canOverride && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {attributes.map((attr) =>
                attr.introducedBy && attr.introducedBy.length > 0 ? (
                  attr.introducedBy.map((trace, idx) => (
                    <TableRow key={`${attr.code}-${idx}`}>
                      {idx === 0 ? (
                        <>
                          <TableCell rowSpan={attr.introducedBy!.length}>
                            <Chip
                              label={attr.name}
                              size="small"
                              color={statusColor[attr.status] ?? 'default'}
                            />
                          </TableCell>
                          <TableCell rowSpan={attr.introducedBy!.length}>
                            {t(`dietary.${lowerFirst(attr.status)}`)}
                          </TableCell>
                          <TableCell rowSpan={attr.introducedBy!.length}>
                            {attr.source}
                          </TableCell>
                        </>
                      ) : null}
                      <TableCell>{trace.componentDescription}</TableCell>
                      <TableCell>
                        <Typography variant="textXS"
                          color="text.secondary">
                          {trace.path}
                        </Typography>
                      </TableCell>
                      {canOverride && idx === 0 && (
                        <TableCell rowSpan={attr.introducedBy!.length}>
                          <Button size="md"
                            startIcon={<Edit />}
                            onClick={() => handleOpenOverride(attr)}>
                            {t('allergens.override')}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow key={attr.code}>
                    <TableCell>
                      <Chip label={attr.name}
                        size="small"
                        color={statusColor[attr.status] ?? 'default'} />
                    </TableCell>
                    <TableCell>{t(`dietary.${lowerFirst(attr.status)}`)}</TableCell>
                    <TableCell>{attr.source}</TableCell>
                    <TableCell colSpan={2}>
                      <Typography variant="textSM"
                        color="text.secondary">—</Typography>
                    </TableCell>
                    {canOverride && (
                      <TableCell>
                        <Button size="md"
                          startIcon={<Edit />}
                          onClick={() => handleOpenOverride(attr)}>
                          {t('allergens.override')}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Override dialog */}
      <Dialog open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('allergens.overrideTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}
            sx={{ mt: 1 }}>
            {selectedAttribute && (
              <Chip label={selectedAttribute.name}
                size="small"
                color={statusColor[selectedAttribute.status] ?? 'default'} />
            )}
            <Select
              value={overrideStatus}
              onChange={(e) => setOverrideStatus(e.target.value as DietaryAttributeStatus)}
              size="small"
              fullWidth
            >
              <MenuItem value={DietaryAttributeStatus.Contains}>{t('dietary.contains')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.MayContain}>{t('dietary.mayContain')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.FreeFrom}>{t('dietary.freeFrom')}</MenuItem>
            </Select>
            <TextField
              multiline
              rows={3}
              fullWidth
              label={t('allergens.overrideReason')}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}
            variant="secondary">
            {t('allergens.overrideCancel')}
          </Button>
          <Button
            onClick={handleApplyOverride}
            variant="primary"
            disabled={overrideSaving || overrideReason.trim() === ''}
          >
            {t('allergens.overrideConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add allergen/intolerance dialog */}
      <Dialog open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('allergens.addTitle', 'Add allergen or intolerance')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}
            sx={{ mt: 1 }}>
            <Select
              value={addCategory}
              onChange={(e) => setAddCategory(e.target.value as DietaryCategory)}
              size="small"
              fullWidth
            >
              <MenuItem value={DietaryCategory.Allergen}>{t('dietary.categories.allergen', 'Allergen')}</MenuItem>
              <MenuItem value={DietaryCategory.Intolerance}>{t('dietary.categories.intolerance', 'Intolerance')}</MenuItem>
            </Select>
            {addCategory === DietaryCategory.Allergen ? (
              <Select
                value={addAllergenId}
                onChange={(e) => setAddAllergenId(e.target.value as number)}
                size="small"
                displayEmpty
                fullWidth
              >
                <MenuItem value=""
                  disabled>{t('allergens.selectAllergen', 'Select an allergen')}</MenuItem>
                {availableMasterAllergens.map((a) => (
                  <MenuItem key={a.id}
                    value={a.id}>{a.name}</MenuItem>
                ))}
              </Select>
            ) : (
              <TextField
                label={t('allergens.intoleranceName', 'Intolerance name')}
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                fullWidth
              />
            )}
            <Select
              value={addStatus}
              onChange={(e) => setAddStatus(e.target.value as DietaryAttributeStatus)}
              size="small"
              fullWidth
            >
              <MenuItem value={DietaryAttributeStatus.Contains}>{t('dietary.contains')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.MayContain}>{t('dietary.mayContain')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.FreeFrom}>{t('dietary.freeFrom')}</MenuItem>
            </Select>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}
            variant="secondary">
            {t('allergens.overrideCancel')}
          </Button>
          <Button
            onClick={handleConfirmAdd}
            variant="primary"
            disabled={addCategory === DietaryCategory.Allergen ? addAllergenId === '' : addName.trim() === ''}
          >
            {t('allergens.addConfirm', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AllergensTab;
